import {
  Layers,
  Sparkles,
  Smartphone,
  Monitor,
  CheckCircle,
  FileSpreadsheet,
  FilePlus2,
  FileText,
  Boxes,
  Activity,
  Palette,
  Droplet,
  LayoutDashboard,
} from "lucide-react";
import { Badge, Button } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";

export type TestUiTabKey =
  | "list"
  | "create"
  | "detail"
  | "blocks"
  | "states"
  | "overview"
  | "core"
  | "mobile"
  | "colors"
  | "kpi-cards";

export interface TestUiHeaderProps {
  currentTab: TestUiTabKey;
  onTabChange: (tab: TestUiTabKey) => void;
  currentScenario: string;
  onScenarioChange: (scenario: string) => void;
  isMobilePreview: boolean;
  onToggleMobilePreview: () => void;
  onOpenCreateModal: () => void;
}

export function TestUiHeader({
  currentTab,
  onTabChange,
  currentScenario,
  onScenarioChange,
  isMobilePreview,
  onToggleMobilePreview,
  onOpenCreateModal,
}: TestUiHeaderProps) {
  const tabs: Array<{ key: TestUiTabKey; label: string; icon: typeof Layers }> = [
    { key: "list", label: "1. Danh sách chuẩn", icon: FileSpreadsheet },
    { key: "create", label: "2. Form tạo mới", icon: FilePlus2 },
    { key: "detail", label: "3. Chi tiết Decision IA", icon: FileText },
    { key: "blocks", label: "4. Blocks & Grids", icon: Boxes },
    { key: "states", label: "5. Các trạng thái", icon: Activity },
    { key: "overview", label: "6. Quy chuẩn Design System", icon: Palette },
    { key: "core", label: "7. Core Components", icon: Layers },
    { key: "mobile", label: "8. Mobile", icon: Smartphone },
    { key: "colors", label: "9. Quy chuẩn Design Color", icon: Droplet },
    { key: "kpi-cards", label: "10. CardKPI Mới vs Cũ", icon: LayoutDashboard },
  ];

  return (
    <header className="sticky top-0 z-30 space-y-3 border-b border-border/80 bg-surface/95 px-4 py-3 backdrop-blur-md transition-colors shadow-sm">
      {/* Top bar: Brand & Title */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-control bg-primary text-on-primary shadow-sm">
            <Sparkles className="size-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="m-0 text-h2 font-bold tracking-tight text-text">
                TM Food ERP — Master Design System & UI Patterns
              </h1>
              <Badge tone="success" label="Chuẩn hóa v2.0" />
            </div>
            <p className="m-0 text-caption text-text-muted">
              Môi trường kiểm chuẩn (Sandbox) cho Màn hình Danh sách, Form Tạo mới, Chi tiết Decision IA & Khối dữ liệu
            </p>
          </div>
        </div>

        {/* Global Action CTAs */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Scenario Selector */}
          <div className="flex items-center gap-1 rounded-control border border-border bg-bg p-1 text-caption">
            <span className="px-2 text-text-muted font-medium">Kịch bản:</span>
            <button
              type="button"
              onClick={() => onScenarioChange("mes")}
              className={cn(
                "rounded px-2.5 py-1 font-medium transition-colors",
                currentScenario === "mes"
                  ? "bg-surface text-primary shadow-xs font-semibold"
                  : "text-text-secondary hover:text-text"
              )}
            >
              Lệnh SX (MES)
            </button>
            <button
              type="button"
              onClick={() => onScenarioChange("sales")}
              className={cn(
                "rounded px-2.5 py-1 font-medium transition-colors",
                currentScenario === "sales"
                  ? "bg-surface text-primary shadow-xs font-semibold"
                  : "text-text-secondary hover:text-text"
              )}
            >
              Đơn bán (SO)
            </button>
          </div>

          {/* Quick Create Trigger */}
          <button
            type="button"
            onClick={onOpenCreateModal}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-caption font-semibold rounded-md bg-primary text-on-primary hover:bg-primary-hover shadow-xs transition-colors cursor-pointer"
          >
            <FilePlus2 className="size-3.5" />
            <span>Mở Form Tạo mới</span>
          </button>

          {/* Responsive Preview Toggle */}
          <button
            type="button"
            onClick={onToggleMobilePreview}
            aria-label="Chuyển đổi giao diện Desktop / Mobile"
            className="inline-flex items-center gap-1.5 h-8 px-3 text-caption font-medium rounded-md border border-border bg-surface text-text hover:bg-bg transition-colors shadow-2xs cursor-pointer"
          >
            {isMobilePreview ? (
              <>
                <Monitor className="size-3.5 text-primary" />
                <span className="hidden sm:inline">Chế độ Desktop</span>
              </>
            ) : (
              <>
                <Smartphone className="size-3.5 text-warning-fg" />
                <span className="hidden sm:inline">Mô phỏng Mobile Card</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <nav
        aria-label="UI Standards Navigation"
        className="flex gap-1 overflow-x-auto border-t border-border/50 pt-2 text-caption scrollbar-none"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = currentTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onTabChange(tab.key)}
              className={cn(
                "flex shrink-0 items-center gap-2 rounded-control px-3 py-2 font-medium transition-all",
                isActive
                  ? "bg-primary-soft text-primary-fg font-semibold shadow-xs"
                  : "text-text-secondary hover:bg-bg hover:text-text"
              )}
            >
              <Icon className={cn("size-4", isActive ? "text-primary" : "text-text-muted")} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </header>
  );
}
